import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { 
  Refresh as RefreshIcon, 
  CheckCircle as ActiveIcon, 
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface Degree {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  level: number;
  is_active: boolean;
}

interface Faculty {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  abbreviation_th?: string;
  abbreviation_en?: string;
  is_active: boolean;
}

interface Major {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  faculty_code: string;
  degree_code: string;
  is_active: boolean;
}

function EducationMasterData() {
  const [tabValue, setTabValue] = useState(0);
  const [degrees, setDegrees] = useState<Degree[]>([]);
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [majors, setMajors] = useState<Major[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, [tabValue]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        await fetchDegrees();
      } else if (tabValue === 1) {
        await fetchFaculties();
      } else if (tabValue === 2) {
        await fetchMajors();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDegrees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-data/degrees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDegrees(response.data);
    } catch (error) {
      console.error('Error fetching degrees:', error);
      throw error;
    }
  };

  const fetchFaculties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-data/faculties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaculties(response.data);
    } catch (error) {
      console.error('Error fetching faculties:', error);
      throw error;
    }
  };

  const fetchMajors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-data/majors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMajors(response.data);
    } catch (error) {
      console.error('Error fetching majors:', error);
      throw error;
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean, type: 'degree' | 'faculty' | 'major') => {
    try {
      const endpoint = type === 'degree' 
        ? `degrees/${id}/active`
        : type === 'faculty'
        ? `faculties/${id}/active`
        : `majors/${id}/active`;

      await axios.patch(
        `${API_URL}/api/master-data/${endpoint}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSnackbar(`${type.charAt(0).toUpperCase() + type.slice(1)} status updated successfully`, 'success');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Degree columns
  const degreeColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 100 },
    { field: 'name_en', headerName: 'Name (EN)', width: 250 },
    { field: 'name_th', headerName: 'Name (TH)', width: 250 },
    { field: 'level', headerName: 'Level', width: 100 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
            <ActiveIcon sx={{ mr: 0.5 }} fontSize="small" /> Active
          </Box>
        ) : (
          <Box sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
            <InactiveIcon sx={{ mr: 0.5 }} fontSize="small" /> Inactive
          </Box>
        )
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Tooltip title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            onClick={() => handleToggleActive(params.row._id, params.row.is_active, 'degree')}
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Faculty columns
  const facultyColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 120 },
    { field: 'name_en', headerName: 'Name (EN)', width: 280 },
    { field: 'name_th', headerName: 'Name (TH)', width: 280 },
    { field: 'abbreviation_en', headerName: 'Abbr (EN)', width: 100 },
    { field: 'abbreviation_th', headerName: 'Abbr (TH)', width: 100 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
            <ActiveIcon sx={{ mr: 0.5 }} fontSize="small" /> Active
          </Box>
        ) : (
          <Box sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
            <InactiveIcon sx={{ mr: 0.5 }} fontSize="small" /> Inactive
          </Box>
        )
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Tooltip title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            onClick={() => handleToggleActive(params.row._id, params.row.is_active, 'faculty')}
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Major columns
  const majorColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 120 },
    { field: 'name_en', headerName: 'Name (EN)', width: 250 },
    { field: 'name_th', headerName: 'Name (TH)', width: 250 },
    { field: 'faculty_code', headerName: 'Faculty', width: 120 },
    { field: 'degree_code', headerName: 'Degree', width: 100 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
            <ActiveIcon sx={{ mr: 0.5 }} fontSize="small" /> Active
          </Box>
        ) : (
          <Box sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
            <InactiveIcon sx={{ mr: 0.5 }} fontSize="small" /> Inactive
          </Box>
        )
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Tooltip title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            onClick={() => handleToggleActive(params.row._id, params.row.is_active, 'major')}
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1">
            Education Master Data
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Degrees" />
          <Tab label="Faculties" />
          <Tab label="Majors" />
        </Tabs>

        {/* Degrees Tab */}
        {tabValue === 0 && (
          <Box>
            <DataGrid
              rows={degrees}
              columns={degreeColumns}
              loading={loading}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              getRowId={(row) => row._id}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              sx={{ height: 600 }}
              disableRowSelectionOnClick
            />
          </Box>
        )}

        {/* Faculties Tab */}
        {tabValue === 1 && (
          <Box>
            <DataGrid
              rows={faculties}
              columns={facultyColumns}
              loading={loading}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              getRowId={(row) => row._id}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              sx={{ height: 600 }}
              disableRowSelectionOnClick
            />
          </Box>
        )}

        {/* Majors Tab */}
        {tabValue === 2 && (
          <Box>
            <DataGrid
              rows={majors}
              columns={majorColumns}
              loading={loading}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              getRowId={(row) => row._id}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              sx={{ height: 600 }}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EducationMasterData;
